import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/pages/_consultant-group-details.scss';
import { getConsultantGroupDetails, updateJoinRequestStatus, updateConsultantGroup, deleteGroup, addUserToGroup, GroupDetailResponse } from '../logic/groupCreation/groupApi';
import { formatDate } from '../utils/dateFormatter';

// Loading Spinner Component
const LoadingSpinner: React.FC = () => (
  <div className="consultant-group-details__spinner">
    <div className="consultant-group-details__spinner-circle"></div>
  </div>
);

const ConsultantGroupDetailsPage: React.FC = () => {
  const { id: groupId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Helper function to get the correct groups route based on current URL
  const getGroupsRoute = () => {
    const currentPath = window.location.pathname;
    if (currentPath.startsWith('/panel/enterprise')) {
      return '/panel/enterprise/overview';
    }
    return '/panel/consultant/group-creation';
  };
  const [group, setGroup] = useState<GroupDetailResponse | null>(null);
  const [counts, setCounts] = useState({ pendingRequests: 0, approvedMembers: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'info', message: string } | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [addMemberEmail, setAddMemberEmail] = useState('');
  const [addMemberError, setAddMemberError] = useState('');
  const [editedDetails, setEditedDetails] = useState({
    description: '',
    tags: [] as string[],
    program: '',
    course: '',
    yearLevel: '',
    maxParticipants: '',
    consultantName: '',
    department: ''
  });

  // Fetch group details
  const fetchGroupDetails = useCallback(async () => {
    if (!groupId) return;

    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await getConsultantGroupDetails(token, groupId);
      setGroup(response.data.group);
      setCounts(response.data.counts);
    } catch (error: any) {
      console.error('Error fetching group details:', error);

      // If group was deleted (404), redirect with friendly message
      if (error.message && (error.message.includes('not found') || error.message.includes('Group not found'))) {
        setNotification({
          type: 'info',
          message: 'This group has been deleted.'
        });

        // Redirect to groups list after a short delay
        setTimeout(() => {
          navigate(getGroupsRoute());
        }, 2000);
      } else {
        setNotification({
          type: 'error',
          message: error.message || 'Failed to load group details. Please try again.'
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [groupId, navigate]);

  // Handle group name update
  const handleUpdateGroupName = async (newName: string) => {
    if (!groupId || !newName.trim()) return;

    try {
      setIsUpdating(true);
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await updateConsultantGroup(token, groupId, { groupName: newName.trim() });

      // Update the local state with the response
      setGroup(response.data.group);
      setCounts(response.data.counts);
      setIsEditingName(false);

      setNotification({
        type: 'success',
        message: 'Group name updated successfully'
      });
    } catch (error: any) {
      console.error('Error updating group name:', error);
      setNotification({
        type: 'error',
        message: error.message || 'Failed to update group name. Please try again.'
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Initialize edit details when entering edit mode
  const initializeEditDetails = () => {
    if (group) {
      setEditedDetails({
        description: group.description,
        tags: [...group.tags],
        program: group.program,
        course: group.course || '',
        yearLevel: group.yearLevel,
        maxParticipants: group.maxParticipants ? group.maxParticipants.toString() : '',
        consultantName: group.consultantName || '',
        department: group.department || ''
      });
    }
  };

  // Handle group details update
  const handleUpdateGroupDetails = async () => {
    if (!groupId) return;

    try {
      setIsUpdating(true);
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const updateData = {
        description: editedDetails.description.trim(),
        tags: editedDetails.tags.filter(tag => tag.trim()),
        program: editedDetails.program.trim() || 'All Programs',
        course: editedDetails.course.trim() || null,
        yearLevel: editedDetails.yearLevel.trim() || 'Any Level',
        maxParticipants: editedDetails.maxParticipants ? Number(editedDetails.maxParticipants) : null,
        consultantName: editedDetails.consultantName.trim(),
        department: editedDetails.department.trim()
      };

      const response = await updateConsultantGroup(token, groupId, updateData);

      // Update the local state with the response
      setGroup(response.data.group);
      setCounts(response.data.counts);
      setIsEditingDetails(false);

      setNotification({
        type: 'success',
        message: 'Group details updated successfully'
      });
    } catch (error: any) {
      console.error('Error updating group details:', error);
      setNotification({
        type: 'error',
        message: error.message || 'Failed to update group details. Please try again.'
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle tag management in edit mode
  const handleAddTag = () => {
    setEditedDetails(prev => ({
      ...prev,
      tags: [...prev.tags, '']
    }));
  };

  const handleRemoveTag = (index: number) => {
    setEditedDetails(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateTag = (index: number, value: string) => {
    setEditedDetails(prev => ({
      ...prev,
      tags: prev.tags.map((tag, i) => i === index ? value : tag)
    }));
  };

  // Handle join request status update
  const handleUpdateJoinRequest = async (userId: string, status: 'approved' | 'rejected') => {
    if (!groupId) return;

    try {
      setIsUpdating(true);
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      await updateJoinRequestStatus(token, groupId, userId, status);

      // Refresh the group details
      await fetchGroupDetails();

      setNotification({
        type: 'success',
        message: `Join request ${status} successfully`
      });
    } catch (error: any) {
      console.error('Error updating join request:', error);
      setNotification({
        type: 'error',
        message: error.message || 'Failed to update join request. Please try again.'
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle group deletion
  const handleDeleteGroup = async () => {
    if (!groupId) {
      console.error('Cannot delete: no groupId');
      return;
    }

    if (!group) {
      console.error('Cannot delete: no group data loaded');
      setNotification({
        type: 'error',
        message: 'Group data not available. Please refresh the page and try again.'
      });
      return;
    }


    try {
      setIsUpdating(true);
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Double-check we're deleting the right group
      if (group.id !== groupId) {
        console.warn('Group ID mismatch! URL param:', groupId, 'Group data:', group.id);
        setNotification({
          type: 'error',
          message: 'There was an issue with the group selection. Please refresh and try again.'
        });
        return;
      }

      await deleteGroup(token, groupId);

      setNotification({
        type: 'success',
        message: 'Group deleted successfully'
      });

      // Navigate back to groups list after a short delay
      setTimeout(() => {
        navigate(getGroupsRoute());
      }, 1500);

    } catch (error: any) {
      console.error('Error deleting group:', error);
      setNotification({
        type: 'error',
        message: error.message || 'Failed to delete group. Please try again.'
      });
    } finally {
      setIsUpdating(false);
      setShowDeleteModal(false);
    }
  };

  // Handle manually adding a user to the group
  const handleAddMember = async () => {
    if (!groupId || !addMemberEmail.trim()) {
      setAddMemberError('Please enter a valid email address');
      return;
    }

    try {
      setIsUpdating(true);
      setAddMemberError('');
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await addUserToGroup(token, groupId, addMemberEmail.trim());

      // Update the local state with the response
      setGroup(response.data.group);
      setCounts(response.data.counts);
      setShowAddMemberModal(false);
      setAddMemberEmail('');

      setNotification({
        type: 'success',
        message: `User ${addMemberEmail.trim()} has been added to the group successfully`
      });
    } catch (error: any) {
      console.error('Error adding member:', error);
      setAddMemberError(error.message || 'Failed to add user to group. Please try again.');
      setNotification({
        type: 'error',
        message: error.message || 'Failed to add user to group. Please try again.'
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Load initial data
  useEffect(() => {
    fetchGroupDetails();
  }, [fetchGroupDetails]);

  // Auto-dismiss notifications after 5 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Get pending requests
  const pendingRequests = group?.joinRequests?.filter(req => req.status === 'pending') || [];

  // Get approved members
  const approvedMembers = group?.joinRequests?.filter(req => req.status === 'approved') || [];

  if (isLoading) {
    return (
      <div className="consultant-group-details">
        <div className="consultant-group-details__loading">
          <LoadingSpinner />
          <span>Loading group details...</span>
        </div>
      </div>
    );
  }

  if (!isLoading && !group) {
    console.warn('Rendering error state: no group data available');
    return (
      <div className="consultant-group-details">
        <div className="consultant-group-details__error">
          <p>Group not found or you don't have permission to view it.</p>
          <button
            className="btn btn-primary"
            onClick={() => navigate(getGroupsRoute())}
          >
            Back to Groups
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="consultant-group-details">
      {/* Header Section */}
      <header className="consultant-group-details__header">
        <div className="consultant-group-details__header-content">
          <div className="consultant-group-details__title-section">
            {isEditingName ? (
              <div className="consultant-group-details__title-edit">
                <span className="consultant-group-details__title-prefix">Group Details:</span>
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  className="consultant-group-details__title-input"
                  placeholder="Enter group name"
                  maxLength={100}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && editedName.trim() && editedName.trim() !== group!.groupName) {
                      handleUpdateGroupName(editedName.trim());
                    } else if (e.key === 'Escape') {
                      setIsEditingName(false);
                      setEditedName(group!.groupName);
                    }
                  }}
                />
                <div className="consultant-group-details__edit-actions">
                  <button
                    className="consultant-group-details__save-btn"
                    onClick={() => handleUpdateGroupName(editedName.trim())}
                    disabled={isUpdating || !editedName.trim() || editedName.trim() === group!.groupName}
                  >
                    <span className="material-symbols-outlined">check</span>
                    Save
                  </button>
                  <button
                    className="consultant-group-details__cancel-btn"
                    onClick={() => {
                      setIsEditingName(false);
                      setEditedName(group!.groupName);
                    }}
                    disabled={isUpdating}
                  >
                    <span className="material-symbols-outlined">close</span>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h1 className="consultant-group-details__title">
                  Group Details: {group!.groupName}
                  <button
                    className="consultant-group-details__edit-btn"
                    onClick={() => {
                      setIsEditingName(true);
                      setEditedName(group!.groupName);
                    }}
                    title="Edit group name"
                  >
                    <span className="material-symbols-outlined">edit</span>
                  </button>
                </h1>
              </>
            )}
          </div>
          <div className="consultant-group-details__stats">
            <div className="consultant-group-details__stat">
              <span className="material-symbols-outlined">group</span>
              <span>Members: {counts.approvedMembers}</span>
            </div>
            <span className="consultant-group-details__separator">•</span>
            <div className="consultant-group-details__stat">
              <span className="material-symbols-outlined">person_add</span>
              <span>Pending Requests: {counts.pendingRequests}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="consultant-group-details__main">
        <div className="consultant-group-details__grid">
          {/* Left Column: Group Information */}
          <div className="consultant-group-details__left-column">
            <div className="consultant-group-details__card">
              <div className="consultant-group-details__card-header">
                <h2 className="consultant-group-details__card-title">
                  Group Information
                  {!isEditingDetails && (
                    <div className="consultant-group-details__card-actions">
                      <button
                        className="consultant-group-details__edit-btn"
                        onClick={() => {
                          initializeEditDetails();
                          setIsEditingDetails(true);
                        }}
                        title="Edit group details"
                      >
                        <span className="material-symbols-outlined">edit</span>
                      </button>
                      <button
                        className="consultant-group-details__delete-btn"
                        onClick={() => setShowDeleteModal(true)}
                        title="Delete group"
                        disabled={isUpdating}
                      >
                        <span className="material-symbols-outlined">delete</span>
                      </button>
                    </div>
                  )}
                  {isEditingDetails && (
                    <div className="consultant-group-details__edit-actions">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleUpdateGroupDetails}
                        disabled={isUpdating}
                      >
                        <span className="material-symbols-outlined">check</span>
                        Save
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setIsEditingDetails(false)}
                        disabled={isUpdating}
                      >
                        <span className="material-symbols-outlined">close</span>
                        Cancel
                      </button>
                    </div>
                  )}
                </h2>
              </div>
              <div className="consultant-group-details__card-content">
                {/* Basic Info */}
                <div className="consultant-group-details__section">
                  {!isEditingDetails ? (
                    <>
                      <div className="consultant-group-details__field">
                        <span className="consultant-group-details__field-label">Description</span>
                        <span className="consultant-group-details__field-value">{group!.description}</span>
                      </div>
                      {group!.tags && group!.tags.length > 0 && (
                        <div className="consultant-group-details__field">
                          <span className="consultant-group-details__field-label">Tags</span>
                          <div className="consultant-group-details__tags">
                            {group!.tags!.map((tag, index) => (
                              <span key={index} className="consultant-group-details__tag">{tag}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="consultant-group-details__field">
                        <label className="consultant-group-details__field-label">Description</label>
                        <textarea
                          className="consultant-group-details__input consultant-group-details__textarea"
                          value={editedDetails.description}
                          onChange={(e) => setEditedDetails(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Enter group description"
                          maxLength={500}
                          rows={3}
                        />
                      </div>
                      <div className="consultant-group-details__field">
                        <label className="consultant-group-details__field-label">Tags</label>
                        <div className="consultant-group-details__tags-input">
                          {editedDetails.tags.map((tag, index) => (
                            <div key={index} className="consultant-group-details__tag-input-group">
                              <input
                                type="text"
                                className="consultant-group-details__input consultant-group-details__tag-input"
                                value={tag}
                                onChange={(e) => handleUpdateTag(index, e.target.value)}
                                placeholder="Enter tag"
                                maxLength={50}
                              />
                              <button
                                type="button"
                                className="consultant-group-details__remove-tag-btn"
                                onClick={() => handleRemoveTag(index)}
                                title="Remove tag"
                              >
                                <span className="material-symbols-outlined">close</span>
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm consultant-group-details__add-tag-btn"
                            onClick={handleAddTag}
                          >
                            <span className="material-symbols-outlined">add</span>
                            Add Tag
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Eligibility - Only show for consultant groups */}
                {group!.groupType === 'consultant' && (
                  <div className="consultant-group-details__section">
                    <h3 className="consultant-group-details__section-title">Eligibility Criteria</h3>
                    {!isEditingDetails ? (
                      <>
                        <div className="consultant-group-details__field">
                          <span className="consultant-group-details__field-label">Program</span>
                          <span className="consultant-group-details__field-value">{group!.program}</span>
                        </div>
                        {group!.course && (
                          <div className="consultant-group-details__field">
                            <span className="consultant-group-details__field-label">Course</span>
                            <span className="consultant-group-details__field-value">{group!.course}</span>
                          </div>
                        )}
                        <div className="consultant-group-details__field">
                          <span className="consultant-group-details__field-label">Year Level</span>
                          <span className="consultant-group-details__field-value">{group!.yearLevel}</span>
                        </div>
                        {group!.maxParticipants && (
                          <div className="consultant-group-details__field">
                            <span className="consultant-group-details__field-label">Max Participants</span>
                            <span className="consultant-group-details__field-value">{group!.maxParticipants}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="consultant-group-details__field">
                          <label className="consultant-group-details__field-label">Program</label>
                          <input
                            type="text"
                            className="consultant-group-details__input"
                            value={editedDetails.program}
                            onChange={(e) => setEditedDetails(prev => ({ ...prev, program: e.target.value }))}
                            placeholder="Enter program name"
                            maxLength={100}
                          />
                        </div>
                        <div className="consultant-group-details__field">
                          <label className="consultant-group-details__field-label">Course (Optional)</label>
                          <input
                            type="text"
                            className="consultant-group-details__input"
                            value={editedDetails.course}
                            onChange={(e) => setEditedDetails(prev => ({ ...prev, course: e.target.value }))}
                            placeholder="Enter course name"
                            maxLength={100}
                          />
                        </div>
                        <div className="consultant-group-details__field">
                          <label className="consultant-group-details__field-label">Year Level</label>
                          <input
                            type="text"
                            className="consultant-group-details__input"
                            value={editedDetails.yearLevel}
                            onChange={(e) => setEditedDetails(prev => ({ ...prev, yearLevel: e.target.value }))}
                            placeholder="Enter year level"
                            maxLength={50}
                          />
                        </div>
                        <div className="consultant-group-details__field">
                          <label className="consultant-group-details__field-label">Max Participants (Optional)</label>
                          <input
                            type="number"
                            className="consultant-group-details__input"
                            value={editedDetails.maxParticipants}
                            onChange={(e) => setEditedDetails(prev => ({ ...prev, maxParticipants: e.target.value }))}
                            placeholder="Enter max participants"
                            min="1"
                            max="10000"
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}


                {/* Consultant/Enterprise Information */}
                {group!.groupType === 'consultant' && (
                  <div className="consultant-group-details__section">
                    <h3 className="consultant-group-details__section-title">Consultant Information</h3>
                    <div className="consultant-group-details__field">
                      <span className="consultant-group-details__field-label">Consultant Name</span>
                      <div className="consultant-group-details__field-with-avatar">
                        <div className="consultant-group-details__avatar">
                          {group!.consultantName ? group!.consultantName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'CO'}
                        </div>
                        <span className="consultant-group-details__field-value">{group!.consultantName || 'Consultant'}</span>
                      </div>
                    </div>
                    <div className="consultant-group-details__field">
                      <span className="consultant-group-details__field-label">Department</span>
                      <span className="consultant-group-details__field-value">{group!.department || 'Not specified'}</span>
                    </div>
                  </div>
                )}

                {group!.groupType === 'enterprise' && (
                  <div className="consultant-group-details__section">
                    <h3 className="consultant-group-details__section-title">Enterprise Information</h3>
                    <div className="consultant-group-details__field">
                      <span className="consultant-group-details__field-label">Group Owner</span>
                      <div className="consultant-group-details__field-with-avatar">
                        <div className="consultant-group-details__avatar">
                          {group!.createdBy?.firstName ? group!.createdBy.firstName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'EU'}
                        </div>
                        <span className="consultant-group-details__field-value">
                          {group!.createdBy?.firstName || group!.createdBy?.email || 'Enterprise User'}
                        </span>
                      </div>
                    </div>
                    <div className="consultant-group-details__field">
                      <span className="consultant-group-details__field-label">Group Type</span>
                      <span className="consultant-group-details__field-value">Enterprise Group</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Membership Management */}
          <div className="consultant-group-details__right-column">
            {/* Membership Requests */}
            <div className="consultant-group-details__card">
              <div className="consultant-group-details__card-header">
                <h2 className="consultant-group-details__card-title">
                  Membership Requests
                  {counts.pendingRequests > 0 && (
                    <span className="consultant-group-details__count-badge">{counts.pendingRequests}</span>
                  )}
                </h2>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => setShowAddMemberModal(true)}
                  disabled={isUpdating}
                  title="Manually add a user to the group"
                >
                  <span className="material-symbols-outlined">person_add</span>
                  Add Member
                </button>
              </div>
              <div className="consultant-group-details__card-content">
                {pendingRequests.length === 0 ? (
                  <div className="consultant-group-details__empty-state">
                    <span className="material-symbols-outlined">inbox</span>
                    <p>No pending requests</p>
                  </div>
                ) : (
                  <div className="consultant-group-details__requests-list">
                    {pendingRequests.map((request) => (
                      <div key={request.user._id} className="consultant-group-details__request-item">
                        <div className="consultant-group-details__request-info">
                          <div className="consultant-group-details__avatar">
                            {(request.user.profile?.preferredName || request.user.profile?.firstName || request.user.email || 'U')[0].toUpperCase()}
                          </div>
                          <div className="consultant-group-details__user-details">
                            <div className="consultant-group-details__user-name">
                              {request.user.profile?.preferredName || request.user.profile?.firstName || 'User'}
                            </div>
                            <div className="consultant-group-details__user-email">{request.user.email}</div>
                            <div className="consultant-group-details__request-time">
                              Applied {formatDate(request.requestedAt)}
                            </div>
                          </div>
                        </div>
                        <div className="consultant-group-details__request-actions">
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleUpdateJoinRequest(request.user._id, 'rejected')}
                            disabled={isUpdating}
                          >
                            <span className="material-symbols-outlined">close</span>
                            Reject
                          </button>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => handleUpdateJoinRequest(request.user._id, 'approved')}
                            disabled={isUpdating}
                          >
                            <span className="material-symbols-outlined">check</span>
                            Accept
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Active Members */}
            <div className="consultant-group-details__card">
              <div className="consultant-group-details__card-header">
                <h2 className="consultant-group-details__card-title">Active Members</h2>
              </div>
              <div className="consultant-group-details__card-content">
                {approvedMembers.length === 0 ? (
                  <div className="consultant-group-details__empty-state">
                    <span className="material-symbols-outlined">group_off</span>
                    <p>No active members yet</p>
                  </div>
                ) : (
                  <div className="consultant-group-details__members-table">
                    <table className="consultant-group-details__table">
                      <thead className="consultant-group-details__table-head">
                        <tr>
                          <th className="consultant-group-details__table-th">Name</th>
                          <th className="consultant-group-details__table-th">Joined Date</th>
                          <th className="consultant-group-details__table-th">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="consultant-group-details__table-body">
                        {approvedMembers.map((member) => (
                          <tr key={member.user._id} className="consultant-group-details__table-row">
                            <td className="consultant-group-details__table-td">
                              <div className="consultant-group-details__member-info">
                                <div className="consultant-group-details__avatar">
                                  {(member.user.profile?.preferredName || member.user.profile?.firstName || member.user.email || 'U')[0].toUpperCase()}
                                </div>
                                <div>
                                  <div className="consultant-group-details__user-name">
                                    {member.user.profile?.preferredName || member.user.profile?.firstName || 'User'}
                                  </div>
                                  <div className="consultant-group-details__user-email">{member.user.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="consultant-group-details__table-td">
                              {formatDate(member.respondedAt || member.requestedAt)}
                            </td>
                            <td className="consultant-group-details__table-td">
                              <div className="consultant-group-details__member-actions">
                                <button
                                  type="button"
                                  className="consultant-group-details__view-member-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/panel/consultant/user/${member.user._id}`);
                                  }}
                                  title="View user and toggle Active/Inactive"
                                >
                                  View
                                </button>
                                <button
                                  className="consultant-group-details__remove-btn"
                                  onClick={() => handleUpdateJoinRequest(member.user._id, 'rejected')}
                                  disabled={isUpdating}
                                  title="Remove Member"
                                >
                                  <span className="material-symbols-outlined">delete</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="consultant-group-details__modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="consultant-group-details__modal" onClick={(e) => e.stopPropagation()}>
            <div className="consultant-group-details__modal-header">
              <h3 className="consultant-group-details__modal-title">Delete Group</h3>
              <button
                className="consultant-group-details__modal-close"
                onClick={() => setShowDeleteModal(false)}
                disabled={isUpdating}
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="consultant-group-details__modal-body">
              <div className="consultant-group-details__modal-icon">
                <span className="material-symbols-outlined">delete_forever</span>
              </div>
              <p className="consultant-group-details__modal-message">
                Are you sure you want to delete <strong>"{group?.groupName}"</strong>?
              </p>
              <p className="consultant-group-details__modal-warning">
                This action cannot be undone. All members will be removed from this group.
              </p>
            </div>
            <div className="consultant-group-details__modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteGroup}
                disabled={isUpdating}
              >
                {isUpdating ? 'Deleting...' : 'Delete Group'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div className="consultant-group-details__modal-overlay" onClick={() => setShowAddMemberModal(false)}>
          <div className="consultant-group-details__modal" onClick={(e) => e.stopPropagation()}>
            <div className="consultant-group-details__modal-header">
              <h3 className="consultant-group-details__modal-title">Add Member to Group</h3>
              <button
                className="consultant-group-details__modal-close"
                onClick={() => {
                  setShowAddMemberModal(false);
                  setAddMemberEmail('');
                  setAddMemberError('');
                }}
                disabled={isUpdating}
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="consultant-group-details__modal-body">
              <p className="consultant-group-details__modal-message">
                Enter the email address of the user you want to add to <strong>"{group?.groupName}"</strong>
              </p>
              <div className="consultant-group-details__form-group">
                <label className="consultant-group-details__form-label">Email Address</label>
                <input
                  type="email"
                  className="consultant-group-details__input"
                  value={addMemberEmail}
                  onChange={(e) => {
                    setAddMemberEmail(e.target.value);
                    setAddMemberError('');
                  }}
                  placeholder="user@example.com"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isUpdating) {
                      handleAddMember();
                    }
                  }}
                  disabled={isUpdating}
                />
              </div>
              {addMemberError && (
                <div className="consultant-group-details__error-message">
                  <span className="material-symbols-outlined">error</span>
                  {addMemberError}
                </div>
              )}
            </div>
            <div className="consultant-group-details__modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowAddMemberModal(false);
                  setAddMemberEmail('');
                  setAddMemberError('');
                }}
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddMember}
                disabled={isUpdating || !addMemberEmail.trim()}
              >
                {isUpdating ? 'Adding...' : 'Add Member'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification && (
        <div className={`consultant-group-details__notification consultant-group-details__notification--${notification.type}`}>
          <span className="material-symbols-outlined">
            {notification.type === 'success' ? 'check_circle' :
              notification.type === 'error' ? 'error' : 'info'}
          </span>
          <span>{notification.message}</span>
          <button
            className="consultant-group-details__notification-close"
            onClick={() => setNotification(null)}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
      )}

    </div>
  );
};

export default ConsultantGroupDetailsPage;